import { round2, sumMoney } from './money.js';

/**
 * Budgettet: tolv maaneder pr. konto i kontoplanen. Det, /budget viser og gemmer, og det,
 * storskaermen maaler omsaetningen mod - se `TvBudgetYear`. Et kvartal er summen af dets maaneder.
 *
 * Rene funktioner, saa skaermen, /budget og Excel-filen regner ens.
 */

export const MONTHS_PER_YEAR = 12;

/** Kvartalets maal: summen af dets tre maaneder. `quarter` er 1-4. */
export function budgetQuarter(months: readonly number[], quarter: number): number {
  const first = (quarter - 1) * 3;
  return sumMoney(months.slice(first, first + 3));
}

/**
 * Et aarsbeloeb fordelt paa tolv maaneder. Oererne, der ikke gaar op, lander i december, saa
 * maanederne tilsammen er praecis beloebet: 100.000 er 11 x 8.333,33 og 8.333,37.
 */
export function splitOverYear(amount: number): number[] {
  const month = round2(amount / MONTHS_PER_YEAR);
  return [
    ...Array<number>(MONTHS_PER_YEAR - 1).fill(month),
    round2(amount - month * (MONTHS_PER_YEAR - 1)),
  ];
}

/** Where a month's revenue target comes from: the budget on /budget, or BC's G/L budget. */
export type BudgetSource = 'codepass' | 'bc';

/**
 * Hvor aarets maal kommer fra, som en del af en saetning: "budgettet på /budget", "BUDGET2026 i
 * Business Central", eller begge med antallet af maaneder fra /budget.
 */
export function budgetSourceText(
  budgetName: string,
  monthSources: readonly BudgetSource[],
): string {
  const own = monthSources.filter((source) => source === 'codepass').length;
  if (own === 0) return `${budgetName} i Business Central`;
  if (own === monthSources.length) return 'budgettet på /budget';
  return `budgettet på /budget for ${own} ${own === 1 ? 'måned' : 'måneder'} og ${budgetName} i Business Central for resten`;
}

/** Kontotyperne i BC's kontoplan. */
export type BudgetAccountType = 'heading' | 'begin_total' | 'posting' | 'end_total' | 'total';

/** En linje i kontoplanen i Business Central. */
export interface BudgetAccount {
  no: string;
  name: string;
  type: BudgetAccountType;
  /** For a total: the accounts it sums, as BC writes it - `1000..1999`, or several joined by `|`. */
  totaling: string;
}

/** En linje paa /budget og i Excel-filen. */
export interface BudgetSheetRow {
  no: string;
  name: string;
  type: BudgetAccountType;
  /** Twelve months, January first. Null on a heading and a Begin-Total, which carry no amounts. */
  months: number[] | null;
  total: number | null;
  /** No account under it has a budget - the saldobalance leaves such lines out. */
  empty: boolean;
}

/**
 * Whether account `no` falls in a BC totaling such as `1000..1999|2500`. Compared as text: the
 * chart's numbers all have four digits, so that orders them as BC does.
 */
export function inTotaling(no: string, totaling: string): boolean {
  return totaling.split('|').some((part) => {
    const [from = '', to] = part.split('..').map((bound) => bound.trim());
    if (to === undefined) return from !== '' && no === from;
    return (from === '' || no >= from) && (to === '' || no <= to);
  });
}

const NO_MONTHS: readonly number[] = Array<number>(MONTHS_PER_YEAR).fill(0);

/**
 * Kontoplanen med budgettet paa: posteringskonti med deres maaneder, og hver total med summen af
 * de posteringskonti, den daekker - som saldobalancen i docs/saldobalance.xlsx. Indtaegter er
 * positive og omkostninger negative, saa "Daekningsbidrag" og "Resultat" er en ren sum.
 */
export function budgetSheet(
  accounts: readonly BudgetAccount[],
  amounts: ReadonlyMap<string, readonly number[]>,
): BudgetSheetRow[] {
  const posting = accounts.filter((account) => account.type === 'posting');
  const monthsOf = (no: string): readonly number[] => amounts.get(no) ?? NO_MONTHS;

  const rows = accounts.map((account): BudgetSheetRow => {
    let months: number[] | null = null;
    let empty = false;
    if (account.type === 'posting') {
      months = [...monthsOf(account.no)];
      empty = months.every((amount) => amount === 0);
    } else if (account.type === 'end_total' || account.type === 'total') {
      const inside = posting.filter((candidate) => inTotaling(candidate.no, account.totaling));
      months = NO_MONTHS.map((_, index) =>
        sumMoney(inside.map((candidate) => monthsOf(candidate.no)[index] ?? 0)),
      );
      empty = inside.every((candidate) => monthsOf(candidate.no).every((amount) => amount === 0));
    }
    return {
      no: account.no,
      name: account.name,
      type: account.type,
      months,
      total: months ? sumMoney(months) : null,
      empty,
    };
  });

  /* A Begin-Total is empty when the End-Total that closes it is. */
  const open: BudgetSheetRow[] = [];
  for (const row of rows) {
    if (row.type === 'begin_total') open.push(row);
    if (row.type === 'end_total') {
      const begin = open.pop();
      if (begin) begin.empty = row.empty;
    }
  }
  return rows;
}
